import { Button, Modal } from "@shopify/polaris"
import type { ComplexAction } from "@shopify/polaris/build/ts/src/types"
import { ArrowLeftIcon } from "@shopify/polaris-icons"
import classNames from "classnames"
import { useEffect } from "react"

import styles from "./index.module.scss"

type stackDataItem = {
  title: string
  children?: string | React.ReactNode
  /** Primary action */
  primaryAction?: ComplexAction;
  /** Collection of secondary actions */
  secondaryActions?: ComplexAction[];
}

interface MultiPopupProps {
  /** 开启状态 */
  open: boolean
  /** 点击返回，回到第一个 适用于分叉多级弹窗 */
  isBackFirst?: boolean
  /** 当前聚焦Index */
  active: number
  /** Modal数据堆栈 */
  stackData: stackDataItem[]
  /** 阻止点击蒙版关闭弹窗 */
  preventCloseOnClickOutside?: boolean
  /**
   * 二级及以上时，右上角 X（以及 Esc）等同于点「返回」，只回退一级而不关闭整个弹窗。
   * 适用于二级面板是「在主表单里挑选内容」的场景：X 在这里的语义是放弃本次挑选，
   * 而不是丢掉主表单已填的内容。默认 false，保持原有行为。
   */
  closeAsBack?: boolean
  /** 上一个 */
  onPrev?: (index: number) => void
  /** 下一个 */
  onNext?: (index: number) => void
  /** 关闭事件 */
  onClose?: () => void
  /** 切换事件 */
  onChange: (index: number) => void
}

// 全局样式ID，用于识别动态创建的style标签
const GLOBAL_STYLE_ID = "multiPopup-prevent-button-pressed"

function MultiPopup ({
  open,
  active,
  onClose,
  onChange,
  stackData,
  preventCloseOnClickOutside = true,
  isBackFirst = true,
  closeAsBack = false,
}: MultiPopupProps){
  const currentModal = stackData[active]

  if (!currentModal){
    console.error("stackData is empty")
    return null
  }

  const goBack = () => {
    onChange(isBackFirst ? 0 : active - 1)
  }

  const handleClose = () => {
    // closeAsBack：二级及以上把关闭动作降级成返回上一级
    if (closeAsBack && active > 0) {
      goBack()
      return
    }

    onClose && onClose()
    onChange(0)
  }

  // 使用useEffect监听蒙层点击事件
  useEffect(() => {
    if (!preventCloseOnClickOutside || !open) return

    const handleBackdropClick = (event: Event) => {
      const target = event.target as HTMLElement

      // 检查是否点击的是Polaris蒙层
      if (target && target.classList.contains("Polaris-Backdrop")) {
        event.stopPropagation()
        event.preventDefault()
      }
    }

    // 延迟添加事件监听器，确保Modal已经渲染
    const timer = setTimeout(() => {
      document.addEventListener("click", handleBackdropClick, true)
    }, 0)

    return () => {
      clearTimeout(timer)
      document.removeEventListener("click", handleBackdropClick, true)
    }
  }, [preventCloseOnClickOutside, open])

  // 使用useEffect动态创建/移除全局样式
  useEffect(() => {
    if (!open || !preventCloseOnClickOutside) {
      // 移除全局样式
      const existingStyle = document.getElementById(GLOBAL_STYLE_ID)

      if (existingStyle) {
        existingStyle.remove()
      }
      return
    }

    // 创建全局样式
    const styleElement = document.createElement("style")

    styleElement.id = GLOBAL_STYLE_ID
    styleElement.textContent = `
      .Polaris-Button--pressed {
        background: none;
      }
    `

    // 添加到head中
    document.head.appendChild(styleElement)

    return () => {
      // 清理时移除样式
      const existingStyle = document.getElementById(GLOBAL_STYLE_ID)

      if (existingStyle) {
        existingStyle.remove()
      }
    }
  }, [preventCloseOnClickOutside, open])

  return <Modal
    open={open}
    onClose={handleClose}
    title={active === 0 ? currentModal.title : <div className={classNames("flex gap-2", styles.modalTitle)}>
      <Button variant="tertiary" icon={ArrowLeftIcon} onClick={goBack}></Button>
      {currentModal.title}
    </div>}
    primaryAction={currentModal.primaryAction}
    secondaryActions={currentModal.secondaryActions}
  >
    <Modal.Section>
      {currentModal.children}
    </Modal.Section>
  </Modal>
}

export default MultiPopup
